import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { ServiceTypeMetadataMap, SINGLE_QUOTES_REGEX } from '@common/constants';
import { BooleanOperator } from '@common/enums';
import { AttachedPolicies, DefaultParams, RetentionPolicyOdata } from '@common/models';
import { ODataPagedResult } from '@common/odata';
import { SharedOdataService } from '@common/services';
import { RetPolicyTag, RetPolicyTagsMap } from '@common/services/smart-search';
import { DisplayNameDictionary, FilterOptions } from '@common/types';
import { filterByWords } from '@common/utils';
import { containsWrapper, getFilterByContains, getFilterByEqFromEnum } from '@common/utils/functions/search';
import { extendedPolicies } from '@common/utils/helper/attached-policies';
import { I18NextPipe } from 'angular-i18next';
import { SmartSearchModel, SmartSearchModelField } from 'mbs-ui-kit';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
export class RetentionPolicyService extends SharedOdataService<RetentionPolicyOdata> {
  private readonly http = inject(HttpClient);
  private readonly i18nPipe = inject(I18NextPipe);

  readonly #tagLegalHold = RetPolicyTagsMap.get(RetPolicyTag.LegalHold);
  readonly #tagName = RetPolicyTagsMap.get(RetPolicyTag.PolicyName);
  readonly #tagType = RetPolicyTagsMap.get(RetPolicyTag.ServiceType);

  private legalHoldType: Partial<DisplayNameDictionary> = {
    true: {
      displayName: this.i18nPipe.transform('common.yes', { format: 'capitalize' })
    },
    false: {
      displayName: this.i18nPipe.transform('common.no', { format: 'capitalize' })
    }
  };

  constructor() {
    super('RetentionPolicy');
  }

  getPolicies(): Observable<ODataPagedResult<RetentionPolicyOdata>> {
    const odataService = this.odataFactory.CreateService<RetentionPolicyOdata>(`/RetentionPolicy`);

    return this.fetchData(odataService);
  }

  getAttachedPolicies(userIds: string[] = [], defaultText = true): Observable<AttachedPolicies> {
    const odataService = this.odataFactory.CreateService<RetentionPolicyOdata>(`GetAttachedPolicies?userIds=${userIds.join(',')}`);

    return this.http.get<AttachedPolicies>(odataService.Query().GetUrl()).pipe(map((policies) => extendedPolicies(policies, defaultText)));
  }

  createPolicy(policy: RetentionPolicyOdata): Observable<RetentionPolicyOdata> {
    return this.odata.Post(policy).Exec();
  }

  updatePolicy(policy: RetentionPolicyOdata): Observable<RetentionPolicyOdata> {
    return this.odata.Put(policy, policy.Id).Exec();
  }

  deletePolicy(id: string): Observable<RetentionPolicyOdata> {
    return this.odata.Delete(id).Exec();
  }

  setDefaultPolicy(policies: DefaultParams): Observable<any> {
    return this.http.post(this.odataFactory.CreateService(`/RetentionPolicy/SetDefault`).Query().GetUrl(), policies);
  }

  updateFilter(searchObj: SmartSearchModel): void {
    const filter: string[] = [];

    if (searchObj?.[this.#tagName.tag]) {
      const options: FilterOptions = {
        model: searchObj[this.#tagName.tag] as SmartSearchModelField[],
        prop: this.#tagName.prop
      };

      filter.push(getFilterByContains(options));
    }

    if (searchObj?.[this.#tagType.tag]) {
      const options: FilterOptions = {
        model: searchObj[this.#tagType.tag] as SmartSearchModelField[],
        prop: this.#tagType.prop,
        enumItems: ServiceTypeMetadataMap
      };

      filter.push(getFilterByEqFromEnum(options));
    }

    if (searchObj?.[this.#tagLegalHold.tag]) {
      const options: FilterOptions = {
        model: searchObj[this.#tagLegalHold.tag] as SmartSearchModelField[],
        prop: this.#tagLegalHold.prop,
        enumItems: this.legalHoldType
      };

      filter.push(getFilterByEqFromEnum(options, false));
    }

    if (searchObj.words?.filter(Boolean)) {
      const term = filterByWords(searchObj).replace(SINGLE_QUOTES_REGEX, '');
      const keys = [this.#tagName.prop];
      const values = keys.map((k) => containsWrapper(k, term));

      filter.push('('.concat(values.join(` ${BooleanOperator.Or} `), ')'));
    }

    this.filter = filter.length > 0 ? filter.join(` ${BooleanOperator.And} `) : '';
  }
}
